import { useFiltersSearchParams } from 'product-list/hooks/useFiltersSearchParams/useFiltersSearchParams'
import type { UseFiltersData } from 'product-list/types/Filters'
import type { ProductListParams } from 'product-list/types/ProductsList'
import { getFilterSelection } from 'product-list/utils/getFiltersData'
import { useMemo } from 'react'
import { DEFAULT_ORDER } from './constants'
import {
	deserializeRange,
	deserializeUserFilter,
} from './serializer/serializer'

export interface UseFiltersDefaultValueParams {
	filtersData: UseFiltersData | undefined
	defaultParams: ProductListParams | undefined
}

export const useFiltersDefaultValues = ({
	filtersData,
	defaultParams,
}: UseFiltersDefaultValueParams) => {
	const { filterSearchParams } = useFiltersSearchParams()

	const defaultFilters = useMemo(() => {
		const initialFilters = filtersData?.filters ?? []
		const filtersToDeserialize =
			filterSearchParams.filters ?? defaultParams?.filters
		const options = filtersToDeserialize
			? deserializeUserFilter(filtersToDeserialize)
			: null
		return options
			? getFilterSelection(initialFilters, options)
			: initialFilters
	}, [defaultParams?.filters, filterSearchParams.filters, filtersData?.filters])

	const defaultOrder =
		filterSearchParams?.order ?? defaultParams?.order ?? DEFAULT_ORDER

	const defaultRangeLimit = useMemo(
		() => filtersData?.range ?? { max: 0, min: 0 },
		[filtersData?.range]
	)

	const defaultRange = useMemo(() => {
		if (filterSearchParams.range) {
			return deserializeRange(filterSearchParams.range)
		}
		if (defaultParams?.range) {
			return deserializeRange(defaultParams?.range)
		}
		return undefined
	}, [filterSearchParams, defaultParams?.range])

	return useMemo(
		() => ({ defaultFilters, defaultOrder, defaultRangeLimit, defaultRange }),
		[defaultFilters, defaultOrder, defaultRangeLimit, defaultRange]
	)
}
